import { useFlag } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AddMiniIcon, AlertWarningCircleIcon, ArrowExpandIcon, EditPencilIcon, LightningBoltIcon, LockIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Item } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { ProTip } from '@calendly/ui/components/pro-tip';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useEventType } from 'features/event_type_provider';
import { groupsManagedByUserSelector, isAdminOrOwnerSelector } from 'selectors/current_user';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { appEditWorkflowUrl, appViewWorkflowUrl } from 'services/workflows/url';
import { WorkflowDeletionModal } from './workflow_deletion_modal';
import { DeleteItem, StackedCards } from '../../components';
import { Subsection } from '../../styles';
const addWorkflowButtonCss = "a1dsw3jo";
const AddWorkflowButton = forwardRef((props, ref) => {
  const {
    url,
    ...restProps
  } = props;
  const as = RouterLink;
  const to = url;
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const decoration = 'secondary-outline';
  return <Button ref={ref} {...restProps} as={as} to={to} target={target} rel={rel} decoration={decoration} className={classNames(addWorkflowButtonCss, props.className)}>
      {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.add_workflow')}
      <ArrowExpandIcon />
    </Button>;
});
const addWorkflowButtonV3Css = "a18ruijh";
const AddWorkflowButtonV3 = forwardRef((props, ref) => {
  const {
    url,
    ...restProps
  } = props;
  const as = RouterLink;
  const to = url;
  return <MinimalButton ref={ref} {...restProps} as={as} to={to} className={classNames(addWorkflowButtonV3Css, props.className)}>
      <>
        <AddMiniIcon />
        {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.add_workflow')}
      </>
    </MinimalButton>;
});
const wrapperCss = "w1g09l88";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const iconTextWrapperCss = "i10hsi0";
const IconTextWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconTextWrapperCss, props.className)} />;
});
const labelContainerCss = "liv56e6";
const LabelContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(labelContainerCss, props.className)} />;
});
const editItemCss = "e1u9mtkq";
const EditItem = forwardRef((props, ref) => {
  const {
    url
  } = props;
  const as = 'a';
  const href = url;
  const target = '_blank';
  const rel = 'noopener noreferrer';
  return <Item ref={ref} {...props} as={as} href={href} target={target} rel={rel} className={classNames(editItemCss, props.className)}>
      <Wrapper>
        <IconTextWrapper>
          <EditPencilIcon size="large" />
          {I18n.t('publisher.action.edit')}
        </IconTextWrapper>
        <ArrowExpandIcon size="large" />
      </Wrapper>
      )
    </Item>;
});
const WorkflowMenu = ({
  workflow,
  openPopup
}) => {
  const url = appEditWorkflowUrl({
    ownerId: workflow.owner_id,
    uuid: workflow.uuid,
    ownerType: workflow.owner_type
  });
  return <>
      <EditItem url={url} />
      <DeleteItem onClick={() => openPopup(workflow.uuid)} />
    </>;
};
const workflowsToCards = (workflows, canManageWorkflows, openPopup, isAdminOrOwner, managedGroupIds) => workflows.map((workflow, idx) => {
  const currentGroupAdmin = workflow.owner_type === 'user_group' && managedGroupIds.includes(workflow.owner_id);
  const personalWorkflow = !['organization', 'user_group'].includes(workflow.owner_type);

  // TODO: ideally this logic should be shared with the workflows list,
  //   might need to drop canManageWorkflows from this check or completely
  const canEdit = canManageWorkflows && (personalWorkflow || currentGroupAdmin || isAdminOrOwner);
  return {
    title: workflow.name,
    icon: <LightningBoltIcon size="large" />,
    htmlId: `workflow-${idx}`,
    menuItems: canEdit && <WorkflowMenu workflow={workflow} openPopup={openPopup} />,
    readOnly: !canEdit,
    sectionHandler: () => !canEdit && window.open(appViewWorkflowUrl({
      uuid: workflow.uuid,
      ownerId: workflow.owner_id,
      ownerType: workflow.owner_type,
      isAdminOrOwner
    }), '_blank')
  };
});
const HelpLink = ({
  children
}) => <Link href="/help/hc/articles/23369903152535" target="_blank" rel="noreferrer noopener" variant="subtle">
    {children}
  </Link>;
const styledTextCss = "s1kkqkqk";
const StyledText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(styledTextCss, props.className)} />;
});
const sectionContainerCss = "s1je5k1o";
const SectionContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionContainerCss, props.className)} />;
});
const AddWorkflowSection = ({
  url
}) => <SectionContainer>
    <BodyText weight="bold">{I18n.t('publisher.event_type_editor.sections.more_options.workflows.add_workflow.header')}</BodyText>
    <StyledText>{I18n.t('publisher.event_type_editor.sections.more_options.workflows.add_workflow.subheader')}</StyledText>
    <AddWorkflowButtonV3 url={url} />
  </SectionContainer>;
const getEventTypeOwnerWorkflowsURL = eventType => {
  const {
    profile,
    isShared,
    creator_id: creatorId,
    isMETParent
  } = eventType;
  const eventTypeOwner = isShared ? {
    id: creatorId,
    type: 'user'
  } : {
    id: profile.owner_id,
    type: profile.owner_type.toLowerCase().replace('page', 'team')
  };
  if (isMETParent) return '/app/admin/managed_workflows';
  return `/app/workflows/${eventTypeOwner.type}/${eventTypeOwner.id}`;
};
export const AdvancedNotificationSection = ({
  workflows
}) => {
  const eventType = useEventType();
  const {
    isMETParent
  } = eventType;
  const url = getEventTypeOwnerWorkflowsURL(eventType);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isLocked = isAnyLocksEnabled(eventType, ['v3_notifications']);
  const managedEventTypeEnabled = useFlag('raven_admin_managed_workflows_feature');
  const managedGroups = useSelector(groupsManagedByUserSelector);
  const managedGroupIds = managedGroups.map(({
    group_id: groupId
  }) => groupId);
  const {
    list,
    canManageWorkflows
  } = workflows;
  const hasWorkflows = list && list.length > 0;
  const [workflowForDeletion, setWorkflowForDeletion] = useState();
  const closePopup = () => {
    setWorkflowForDeletion(null);
  };
  return <Subsection id="advanced-notifications">
      <LabelContainer>
        <BodyText weight="bold">
          {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.title')}
        </BodyText>
        <ProTip spacingLeft={2} spacingRight={8}>
          {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.tooltip')}
        </ProTip>
      </LabelContainer>
      {!canManageWorkflows && <ContextualBanner icon={LockIcon}>
          {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.only_owner_can_make_changes')}
        </ContextualBanner>}
      {managedEventTypeEnabled && isMETParent && !isLocked && <ContextualBanner variant="subtle" icon={AlertWarningCircleIcon}>
          {HtmlParser(I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.assignees_can_add_workflows'), {
        HelpLink
      })}
        </ContextualBanner>}
      <StackedCards cards={workflowsToCards(list || [], canManageWorkflows, setWorkflowForDeletion, isAdminOrOwner, managedGroupIds)} />
      {workflowForDeletion && <WorkflowDeletionModal workflows={workflows} workflowUuid={workflowForDeletion} closePopup={closePopup} />}
      {canManageWorkflows && !isLocked && (hasWorkflows ? <AddWorkflowButton url={url} /> : <AddWorkflowSection url={url} />)}
    </Subsection>;
};

require("./advanced_notifications_section.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./advanced_notifications_section.js");